import {
  HttpClient,
  HttpContext,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http'
import { Injectable } from '@angular/core'
import {
  fromUtc,
  HANDLE_ERROR_AND_THROW,
  HELIOS_COMMAND_HTTP_TOKEN,
} from '@helios/utils'
import { Observable } from 'rxjs'
import { map, mergeAll, take, toArray } from 'rxjs/operators'
import {
  Break,
  BreakAvailableRestrictedColumns,
  BreakCodeResponse,
  BreakHistory,
  BreakNoteHandling,
  BreakResponse,
  BreakResponseObject,
  BreakSetValues,
  ExportBreaksToExcel,
  FindBreaksForm,
  UserFeatureAccess,
} from '../model/break.model'
import { BreakEmailData } from '../model/copilot.model'
import { MultiBreaksService } from './multi-breaks.service'

export const BREAKS_URL = 'helios-breaks'
export const BREAKS_BULK_URL = `${BREAKS_URL}/bulk`
export const BREAKS_SET_VALUES = `${BREAKS_URL}/setValues`
export const BREAKS_RESTRICTED_COLUMNS = `${BREAKS_URL}/breakRestrictedColumns`
export const BREAKS_CLIENT_URL = `${BREAKS_URL}/breakCodes`
export const BREAKS_USER_FEATURE_ACCESS_URL = `${BREAKS_URL}/userFeatureAccess`
const MAX_BREAKS = 10000

export interface BreaksUpdateRequest {
  breakIds: string[]
  type: BreakUpdateActionType
  expirationDate?: Date
  reconDate?: Date
  expireInBusinessDays?: number
  note?: string
  actionCode?: string
  descriptionCode?: string
  restrictedBreaks?: string[]
  noteHandling: BreakNoteHandling
  preserveExistingBreakCodes?: boolean
}

export enum BreakUpdateActionType {
  EDIT_NOTE = 'EDIT_NOTE',
  IGNORE = 'IGNORE',
  WAIT = 'WAIT',
  UNRESOLVE = 'UNRESOLVE',
}

/**
 * Constructor for a BreaksUpdateRequest object.
 */
export function createBreaksUpdateRequest(
  breakIds: string[],
  type: BreakUpdateActionType,
  note: string,
  noteHandling: BreakNoteHandling,
  actionCode?: string,
  descriptionCode?: string,
  restrictedBreaks?: string[],
  options?: {
    expirationDate?: Date
    expireInBusinessDays?: number
  },
  reconDate?: Date,
  preserveExistingBreakCodes = false,
): BreaksUpdateRequest {
  return {
    breakIds,
    type,
    note,
    noteHandling: note.length !== 0 ? noteHandling : BreakNoteHandling.APPEND,
    expirationDate: options?.expirationDate,
    reconDate: reconDate,
    actionCode,
    descriptionCode,
    expireInBusinessDays: options?.expireInBusinessDays,
    restrictedBreaks,
    preserveExistingBreakCodes,
  }
}

@Injectable({
  providedIn: 'root',
})
export class HttpMultiBreaksService extends MultiBreaksService {
  getBreakCodes(accountIds: number[]): Observable<BreakCodeResponse> {
    return this.http.post<BreakCodeResponse>(
      `${BREAKS_CLIENT_URL}/accounts/active`,
      accountIds,
    )
  }
  constructor(private readonly http: HttpClient) {
    super()
  }

  getBreaks(findBreaksForm: FindBreaksForm): Observable<BreakResponse> {
    let accountIdsValue: number[] = []
    let ultimateParentIdsValue: number[] = []
    let clientIdsValue: number[] = []
    let breakIds: string[] = []
    if (findBreaksForm.rootType === 'accountIds') {
      accountIdsValue = findBreaksForm.rootIds
    } else if (findBreaksForm.rootType === 'ultimateParentIds') {
      ultimateParentIdsValue = findBreaksForm.rootIds
    } else if (findBreaksForm.rootType === 'clientIds') {
      clientIdsValue = findBreaksForm.rootIds
    } else if (findBreaksForm.rootType === 'breakIds') {
      breakIds = findBreaksForm.breakIds
    }
    let assetIds: string[] | number[] = []
    let securityIds: string[] | number[] = []
    if (findBreaksForm.searchIdType === 'assetIds') {
      assetIds = findBreaksForm.searchByIds
    }
    if (findBreaksForm.searchIdType === 'securityIds') {
      securityIds = findBreaksForm.searchByIds
    }

    return this.http.post<BreakResponse>(
      BREAKS_URL,
      {
        filter: {
          accountIds: accountIdsValue,
          clientIds: clientIdsValue,
          parentClientIds: ultimateParentIdsValue,
          assetIds: assetIds,
          securityIds: securityIds,
          breakTypes: findBreaksForm.breakTypes,
          breakStatuses: findBreaksForm.breakStatus,
          breakPriorities: findBreaksForm.breakPriority,
          breakIds: breakIds,
          breakCategories: findBreaksForm.breakCategories,
        },
      },
      {
        params: {
          maxBreaks: MAX_BREAKS,
        },
        context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
      },
    )
  }

  getBreak(identifier: string): Observable<Break> {
    return this.http.get<Break>(`${BREAKS_URL}/${identifier}`)
  }

  accept(
    breakIds: string[],
    note: string,
    noteHandling: BreakNoteHandling,
    restrictedBreaks?: string[],
    actionCode?: string,
    descriptionCode?: string,
    preserveExistingBreakCodes = false,
  ): Observable<BreakResponseObject> {
    const request: BreaksUpdateRequest = createBreaksUpdateRequest(
      breakIds,
      BreakUpdateActionType.IGNORE,
      note,
      noteHandling,
      actionCode,
      descriptionCode,
      restrictedBreaks,
      undefined,
      undefined,
      preserveExistingBreakCodes,
    )
    const options = {
      context: new HttpContext().set(HELIOS_COMMAND_HTTP_TOKEN, true),
    }
    return this.http.put<BreakResponseObject>(BREAKS_BULK_URL, request, options)
  }

  holdToDate(
    breakIds: string[],
    expirationDate: Date,
    note: string,
    noteHandling: BreakNoteHandling,
    restrictedBreaks?: string[],
    actionCode?: string,
    descriptionCode?: string,
    preserveExistingBreakCodes = false,
  ): Observable<BreakResponseObject> {
    const request: BreaksUpdateRequest = createBreaksUpdateRequest(
      breakIds,
      BreakUpdateActionType.WAIT,
      note,
      noteHandling,
      actionCode,
      descriptionCode,
      restrictedBreaks,
      { expirationDate },
      undefined,
      preserveExistingBreakCodes,
    )
    const options = {
      context: new HttpContext().set(HELIOS_COMMAND_HTTP_TOKEN, true),
    }
    return this.http.put<BreakResponseObject>(BREAKS_BULK_URL, request, options)
  }

  holdForDays(
    breakIds: string[],
    expireInBusinessDays: number,
    note: string,
    noteHandling: BreakNoteHandling,
    restrictedBreaks?: string[],
    reconDate?: Date,
    actionCode?: string,
    descriptionCode?: string,
    preserveExistingBreakCodes = false,
  ): Observable<BreakResponseObject> {
    const request: BreaksUpdateRequest = createBreaksUpdateRequest(
      breakIds,
      BreakUpdateActionType.WAIT,
      note,
      noteHandling,
      actionCode,
      descriptionCode,
      restrictedBreaks,
      { expireInBusinessDays },
      reconDate,
      preserveExistingBreakCodes,
    )
    const options = {
      context: new HttpContext().set(HELIOS_COMMAND_HTTP_TOKEN, true),
    }
    return this.http.put<BreakResponseObject>(BREAKS_BULK_URL, request, options)
  }

  unresolve(breakIds: string[]): Observable<BreakResponseObject> {
    const request: BreaksUpdateRequest = {
      breakIds,
      type: BreakUpdateActionType.UNRESOLVE,
      note: '',
      noteHandling: BreakNoteHandling.APPEND,
    }

    const options = {
      context: new HttpContext().set(HELIOS_COMMAND_HTTP_TOKEN, true),
    }
    return this.http.put<BreakResponseObject>(BREAKS_BULK_URL, request, options)
  }

  updateNote(
    breakIds: string[],
    note: string,
    noteHandling: BreakNoteHandling,
    actionCode?: string,
    descriptionCode?: string,
    preserveExistingBreakCodes = false,
  ): Observable<BreakResponseObject> {
    const request: BreaksUpdateRequest = createBreaksUpdateRequest(
      breakIds,
      BreakUpdateActionType.EDIT_NOTE,
      note,
      noteHandling,
      actionCode,
      descriptionCode,
      undefined,
      undefined,
      undefined,
      preserveExistingBreakCodes,
    )
    // An entered note overwrites (REPLACE) every selected break's note; an EMPTY
    // note is downgraded to APPEND by createBreaksUpdateRequest, i.e. a no-op that
    // leaves existing notes untouched (so a notes-required code is never stripped).
    const options = {
      context: new HttpContext().set(HELIOS_COMMAND_HTTP_TOKEN, true),
    }
    return this.http.put<BreakResponseObject>(BREAKS_BULK_URL, request, options)
  }

  getSetValues(): Observable<BreakSetValues> {
    return this.http.get<BreakSetValues>(BREAKS_SET_VALUES)
  }

  getBreakHistory(breakId: string): Observable<BreakHistory[]> {
    return this.http
      .get<BreakHistory[]>(`${BREAKS_URL}/${breakId}/status/history`, {
        context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
      })
      .pipe(
        mergeAll(),
        map(history => ({
          ...history,
          dateTime: fromUtc(history.dateTime),
        })),
        toArray(),
      )
  }

  getBreakEmailData(breakId: string): Observable<BreakEmailData> {
    return this.http.get<BreakEmailData>(`${BREAKS_URL}/${breakId}/email-data`)
  }

  exportBreaks(request: ExportBreaksToExcel): Observable<Blob> {
    const options = {
      responseType: 'blob' as const,
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
    }
    return this.http.post(`${BREAKS_URL}/export`, request, options)
  }

  getAvailableRestrictedColumns(): Observable<
    BreakAvailableRestrictedColumns[]
  > {
    return this.http.get<BreakAvailableRestrictedColumns[]>(
      BREAKS_RESTRICTED_COLUMNS,
    )
  }

  async getUserFeatureAccess(): Promise<UserFeatureAccess> {
    try {
      return await this.http
        .get<UserFeatureAccess>(BREAKS_USER_FEATURE_ACCESS_URL, {
          context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
        })
        .pipe(take(1))
        .toPromise()
    } catch (e) {
      if (e instanceof HttpErrorResponse) {
        console.error(
          `(${e.status}) Error getting user feature access for breaks panel.`,
        )
      }
      /**
       * According to the documentation, it isn't a big deal if those without feature access are able to have access
       * to these features in the UI. Having the defaults set to true allows for backwards compatibility and prevents
       * users from losing access to these critical features in the event of a breaks server issue.
       */
      return {
        findBreaks: true,
        findCurrentSessionBreaks: true,
      }
    }
  }
}
